import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import { useSurveyParams } from '../context/SurveyParamsContext'
import 'survey-core/i18n/traditional-chinese'
import {
  convertVisibleIf,
  convertQuestionMappings,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = 'all'
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  const userIdRef = useRef(null)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)
  // 名單式調查參數
  const { sfdSamplerId, sfdPId } = useSurveyParams()

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  // 複製自 P260917191016735，題號調整如下（舊→新）：
  // 1~31 不變；舊32、33刪，新增新32~34；舊34~93 +1
  // 舊94、95刪，新增新95、96；舊96~98 +1
  // 舊99刪，新增新100~102；舊100~127 +3
  // 舊128、129刪，新增新131~133；舊130~134 +4
  // 舊135、136刪，新增新139~141（9/30）；舊137~145 +5
  // 舊146~151刪，新增新151~159；舊152、153（手機、信箱）合併為新160 聯絡電話
  // 舊82、83（B9-5 恢復類型／原因）順序對調為新83 原因、新84 類型
  // 新增題皆為單選，「其他，請說明」為題內選項，不需另設顯示條件
  // 以下皆為 1-based 寫法，convertVisibleIf 會自動 -1 還原為 0-based

  // A1(q12) 婚姻狀況：1未婚 2已婚 3未婚同居 4分居 5離婚 6喪偶
  // 有配偶或同居伴侶（A6、B7、D7、D9 限定查填）
  const hasPartner =
    '({q12} = 1 and ({q13} = 2 or {q13} = 3)) or ' +
    '({q12} = 2) or ' +
    '({q12} = 3) or ' +
    '({q12} = 4) or ' +
    '({q12} = 5 and ({q17} = 2 or {q17} = 3)) or ' +
    '({q12} = 6 and ({q18} = 2 or {q18} = 3))'

  // A4-1/A4-2/A4-3 限「有子女且子女年齡含 0~未滿6歲」者（A4 註記：2-1至2-3皆未勾選跳答A5）
  const hasChildUnder6 = '({q21} = 2) and ({q24} anyof [1, 2, 3])'

  // B1(q51)=1 有工作 → 續答 B2~B6-1；(2-1)或答完(2-2)一律跳答 B7
  const isEmployed = '{q51} = 1'

  const visibleIfConditionsInit = convertVisibleIf({
    // ===== 基本資料 =====
    // Q2-1(q3)=(1)無/不識字 → 跳答 Q3-1，即跳過 Q2-2
    q4: '{q3} <> 1',
    // Q4-1(q8) 勾「(1)獨居」(none) → 跳答 Q4-3，即跳過 Q4-2
    q9: '{q8} notcontains ["none"]',

    // ===== A 部分：婚育狀況 =====
    // A1 六個分支子題
    q13: '{q12} = 1',
    q14: '{q12} = 2',
    q15: '{q12} = 3',
    q16: '{q12} = 4',
    q17: '{q12} = 5',
    q18: '{q12} = 6',
    // A2 未婚原因：已婚/分居跳答 A4、離婚/喪偶跳答 A3，皆不問
    q19: '{q12} = 1 or {q12} = 3',
    // A3 結婚再婚意願：限未婚、未婚同居、離婚、喪偶（題幹自述限制）
    q20: '{q12} = 1 or {q12} = 3 or {q12} = 5 or {q12} = 6',
    // A4 子女人數與年齡
    q22: '{q21} = 2',
    q23: '{q21} = 2',
    q24: '{q21} = 2',
    q25: '({q21} = 2) and ({q24} anyof [1])',
    q26: '({q21} = 2) and ({q24} anyof [2])',
    q27: '({q21} = 2) and ({q24} anyof [3])',
    q28: '({q21} = 2) and ({q24} anyof [4])',
    q29: '({q21} = 2) and ({q24} anyof [5])',
    q30: '({q21} = 2) and ({q24} anyof [6])',
    // A4-1 托育花費 / A4-2 照顧方式 / A4-3 育嬰留停
    q31: hasChildUnder6,
    // A4-2 主要／次要／再次要照顧方式（新32~34）
    q32: hasChildUnder6,
    q33: hasChildUnder6,
    // A4-2 次要(q33) 選「(20)無次要照顧方式」(第19項) → 不問再次要(q34)
    q34: '(' + hasChildUnder6 + ') and ({q33} <> 19)',
    q35: hasChildUnder6,
    // A5 理想子女數
    q37: '{q36} = 1',
    q38: '({q36} = 1) and ({q37} = 1)',
    q39: '({q36} = 1) and ({q37} = 1)',
    q40: '({q36} = 1) and ({q37} = 2)',
    q41: '{q36} = 2',
    q42: '{q36} = 2',
    // A6 配偶／伴侶的理想子女數【限有配偶(同居伴侶)者查填】
    q43: hasPartner,
    q44: '(' + hasPartner + ') and ({q43} = 1)',
    q45: '(' + hasPartner + ') and ({q43} = 1) and ({q44} = 1)',
    q46: '(' + hasPartner + ') and ({q43} = 1) and ({q44} = 1)',
    q47: '(' + hasPartner + ') and ({q43} = 1) and ({q44} = 2)',
    q48: '(' + hasPartner + ') and ({q43} = 2)',
    q49: '(' + hasPartner + ') and ({q43} = 2)',

    // ===== B 部分：就業與工作 =====
    // B1(q51)=2 沒有 → B1-2；(2-2)沒找工作 → 原因與最主要一項
    q52: '{q51} = 2',
    q53: '({q51} = 2) and ({q52} = 2)',
    q54: '({q51} = 2) and ({q52} = 2)',
    // B2~B6-1 限有工作者
    q55: isEmployed,
    q56: isEmployed,
    q57: isEmployed,
    q58: isEmployed,
    q59: isEmployed,
    q60: isEmployed,
    q61: isEmployed,
    q62: isEmployed,
    // B7 配偶有無工作【限有配偶(同居伴侶)者查填】
    q63: hasPartner,
    // B9 六列：(2)沒有 → 跳下一列；A欄選「未曾恢復工作」→ 跳下一列
    // A欄「在離職__年__月後曾恢復工作」為 other 選項
    q66: '{q65} = 1',
    q67: '({q65} = 1) and ({q66} = "other")',
    q68: '({q65} = 1) and ({q66} = "other")',
    q70: '{q69} = 1',
    q71: '({q69} = 1) and ({q70} = "other")',
    q72: '({q69} = 1) and ({q70} = "other")',
    q74: '{q73} = 1',
    q75: '({q73} = 1) and ({q74} = "other")',
    q76: '({q73} = 1) and ({q74} = "other")',
    q78: '{q77} = 1',
    q79: '({q77} = 1) and ({q78} = "other")',
    q80: '({q77} = 1) and ({q78} = "other")',
    q82: '{q81} = 1',
    q83: '({q81} = 1) and ({q82} = "other")',
    q84: '({q81} = 1) and ({q82} = "other")',
    q86: '{q85} = 1',
    q87: '({q85} = 1) and ({q86} = "other")',
    q88: '({q85} = 1) and ({q86} = "other")',

    // ===== C 部分：經濟安全 =====
    // C1(q89) 選(1)沒有收入 → 跳轉至 C3(q93)，即跳過 C1-2、C2、C2-2
    q90: '{q89} = 2',
    q91: '{q89} <> 1',
    q92: '({q89} <> 1) and ({q91} = 2)',
    q94: '{q93} = 2',
    q98: '{q97} = 2',

    // ===== D 部分：家庭照顧 =====
    // D2 次要(q101) 選「(17)無次要處理者」(第17項) → 不問再次要(q102)
    q102: '{q101} <> 17',
    // D3 各列：A欄「有」為 other 選項；B欄選(2)我是主要照顧者才問輪替照顧者
    q104: '{q103} = "other"',
    q105: '({q103} = "other") and ({q104} = 2)',
    q106: '({q103} = "other") and ({q104} = 2) and ({q105} = 1)',
    q108: '{q107} = "other"',
    q109: '({q107} = "other") and ({q108} = 2)',
    q110: '({q107} = "other") and ({q108} = 2) and ({q109} = 1)',
    q112: '{q111} = "other"',
    q113: '({q111} = "other") and ({q112} = 2)',
    q114: '({q111} = "other") and ({q112} = 2) and ({q113} = 1)',
    q116: '{q115} = "other"',
    q117: '({q115} = "other") and ({q116} = 2)',
    q118: '({q115} = "other") and ({q116} = 2) and ({q117} = 1)',
    // D7 配偶無酬照顧時間【限有配偶或同居伴侶者】
    q124: hasPartner,
    q125: hasPartner,
    q126: hasPartner,
    q127: hasPartner,
    q128: hasPartner,
    q129: hasPartner,
    // D9 提升配偶參與家務意願（主要／次要／再次要）【限有配偶(同居伴侶)者查填】
    q131: hasPartner,
    q132: hasPartner,
    // D9 次要(q132) 選「(8)無次要方式」(第7項) → 不問再次要(q133)
    q133: '(' + hasPartner + ') and ({q132} <> 7)',

    // ===== E 部分：社會參與 =====
    // E1(q134) 勾「(8)都沒有」(none) 才續問 E2，其餘跳答 F1
    q135: '{q134} anyof ["none"]',

    // ===== F 部分：醫療健康與求助 =====
    // F3(q138)=2 有困擾 → F3-2 主要／次要／再次要困擾
    q139: '{q138} = 2',
    q140: '{q138} = 2',
    // F3-2 次要(q140) 選「(10)無次要困擾」(第9項) → 不問再次要(q141)
    q141: '({q138} = 2) and ({q140} <> 9)',
    // F7(q145) 選「(1)沒有達到規律運動」(other) 才問 F8，選「(2)有」跳答 G1
    q146: '{q145} = "other"',


    // ===== H 部分：優先順序（次要選「無第二優先」→ 不問第三優先）=====
    // H1 q152 第16項「(17)無第二優先」
    q153: '{q152} <> 16',
    // H2 q155 第11項「(12)無第二優先」
    q156: '{q155} <> 11',
    // H3 q158 第9項「(10)無第二優先」
    q159: '{q158} <> 9',
  })

  const notRequired = null
  const mappedRateChoice = null
  const pageBreakQuestions = null

  // 本卷無同意書題（來源 P260722164808465 的 q1），不設結束條件
  const triggersConditionsInit = null
  // 選項繼承：複選的原因題 → 「最主要一項」單選題，只帶出前題勾選的選項
  const questionMappings = convertQuestionMappings({
    q41: 'q42', // A5-2 不想有小孩原因 → A5-2-a 最主要一項
    q48: 'q49', // A6-2 配偶不想有小孩原因 → A6-2-a 最主要一項
    q53: 'q54', // B1-2-2 沒找工作原因 → B1-2-2-a 最主要一項
  })

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    setWebAuthType,
    sfdSamplerId,
    sfdPId,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    webAuthType,
    sfdSamplerId,
  })

  useEffect(() => {
    if (agreeSurvey) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [agreeSurvey])

  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {}

  // 結尾語：本案客製（不使用 EZChoice 預設結語）
  const endSurveyMsg = (survey) => {
    setCompletionMessage(
      `<div>謝謝您的協助，如有任何問題請致電02-23315133#601~604典通股份有限公司，或致電1999（外縣市02-27208889）#6969社會局婦女福利及兒童托育科詢問。</div>`
    )
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    userIdRef,
    enableProgressSaving: webAuthType > 0 || !!sfdSamplerId,
    webAuthType,
    sfdSamplerId,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
